import React, { useState, useEffect, useRef } from 'react';
import { Box, Tabs, Tab, useMediaQuery, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import NewspaperIcon from '@mui/icons-material/Newspaper';

export interface SectionItem {
  id: string;
  label: string;
}

interface SectionNavigationProps {
  sections: SectionItem[];
  activeSection: string;
  onSectionChange: (sectionId: string) => void;
  stickyTop?: number;
}

// コンテナの最大幅（MUIのlg = 1200px）
const CONTAINER_MAX_WIDTH = 1140;
// サイドバーの幅
const SIDEBAR_WIDTH = 160;
// サイドバーとコンテナの間隔
const SIDEBAR_GAP = 16;
// 必要な最小余白（サイドバー幅 + 間隔）
const MIN_MARGIN_NEEDED = SIDEBAR_WIDTH + SIDEBAR_GAP;

const SectionNavigation: React.FC<SectionNavigationProps> = ({
  sections,
  activeSection,
  onSectionChange,
  stickyTop = 80
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [isVisible, setIsVisible] = useState(false);
  const [hasEnoughSpace, setHasEnoughSpace] = useState(false);
  const [sidebarLeft, setSidebarLeft] = useState(0);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // 画面幅に応じてサイドバーの位置と表示を計算
  useEffect(() => {
    if (!isDesktop) {
      setHasEnoughSpace(false);
      return;
    }

    const calculatePosition = () => {
      const viewportWidth = window.innerWidth;
      // コンテナの左端位置を計算
      const containerLeft = Math.max((viewportWidth - CONTAINER_MAX_WIDTH) / 2, 0);
      // 左側の余白
      const leftMargin = containerLeft;

      // 十分なスペースがあるかチェック（左右両方に余白が必要）
      const hasSpace = leftMargin >= MIN_MARGIN_NEEDED;
      setHasEnoughSpace(hasSpace);

      if (hasSpace) {
        // サイドバーの左位置 = コンテナ左端 - サイドバー幅 - 間隔
        setSidebarLeft(containerLeft - SIDEBAR_WIDTH - SIDEBAR_GAP);
      }
    };

    calculatePosition();
    window.addEventListener('resize', calculatePosition);
    return () => window.removeEventListener('resize', calculatePosition);
  }, [isDesktop]);

  // スクロール位置に応じてサイドバーの表示を制御
  useEffect(() => {
    if (!isDesktop || !hasEnoughSpace) {
      setIsVisible(false);
      return;
    }

    const handleScroll = () => {
      // 400px以上スクロールしたらサイドバーを表示
      setIsVisible(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDesktop, hasEnoughSpace]);

  const handleSectionClick = (sectionId: string) => {
    onSectionChange(sectionId);
  };

  // モバイル または 余白が足りない場合: 従来のタブ表示
  if (!isDesktop || !hasEnoughSpace) {
    return (
      <Tabs
        value={activeSection}
        onChange={(_, value) => onSectionChange(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
            minHeight: '48px',
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-selected': {
              color: '#fff',
            }
          },
          '& .MuiTabs-indicator': {
            backgroundColor: 'primary.main',
            height: '3px',
          }
        }}
      >
        {sections.map((section) => (
          <Tab key={section.id} label={section.label} value={section.id} />
        ))}
      </Tabs>
    );
  }

  // セクションIDに対応するアイコンを取得
  const getIcon = (sectionId: string, isActive: boolean) => {
    const iconStyle = {
      fontSize: '1rem',
      color: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.4)',
      transition: 'color 0.2s ease',
    };
    switch (sectionId) {
      case 'basic':
        return <InfoOutlinedIcon sx={iconStyle} />;
      case 'achievements':
        return <EmojiEventsOutlinedIcon sx={iconStyle} />;
      case 'news':
        return <NewspaperIcon sx={iconStyle} />;
      default:
        return null;
    }
  };

  // PC かつ 余白が十分: 左側追従サイドバー
  return (
    <Box
      ref={sidebarRef}
      sx={{
        position: 'fixed',
        left: sidebarLeft,
        top: stickyTop,
        width: `${SIDEBAR_WIDTH}px`,
        py: 2,
        zIndex: 100,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
        }}
      >
        {sections.map((section) => {
          const isActive = activeSection === section.id;
          return (
            <Box
              key={section.id}
              onClick={() => handleSectionClick(section.id)}
              sx={{
                py: 0.5,
                px: 1,
                cursor: 'pointer',
                borderRadius: '4px',
                backgroundColor: isActive ? 'rgba(255, 255, 255, 0.04)' : 'transparent',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  '& .MuiSvgIcon-root': {
                    color: isActive ? undefined : 'rgba(255, 255, 255, 0.5)',
                  }
                }
              }}
            >
              {getIcon(section.id, isActive)}
              <Box
                component="span"
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: isActive ? 400 : 400,
                  color: isActive ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.4)',
                  transition: 'color 0.2s ease',
                }}
              >
                {section.label}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SectionNavigation;
